// Block K (UI-22): Invoicing > Expenses on the v2 kit. Renders inside InvoicingLayout (which owns
// the page header and the section tabs), so it opens with its SectionHead.
//
// NOTHING IS CALLED DONE BEFORE THE SERVER SAYS SO. Save, edit, approve and reject used to show
// their success toast and close the form before the request went out, with a console.error and a
// silent refresh as the only failure handling: an expense that was never recorded looked recorded.
// Each now keeps its optimistic row, waits for the server, toasts on success, and on a failure
// puts the row back and says the server's words (the form stays open, holding what was typed).
// Delete waits behind Undo (decision 7); reject asks for its reason in a dialog, not window.prompt.
//
// MONEY (decision 72): an expense has no currency of its own, so it is in the business's base
// currency. Totals are still summed per currency ("£X plus €Y") so a per-expense currency, when it
// exists, can never be added across.
import { useState, useMemo, useEffect } from "react";
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import useChatIntro from '../../tours/useChatIntro';
import { EXPENSE_CATEGORIES } from "../../services/expenseService";
import { useExpenses } from "../../contexts/ExpensesContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { api } from "../../api/client";
import { getProjects } from "../../services/projectService";
import { useContacts } from "../../contexts/ContactsContext";
import { messageFromError } from "../../utils/apiError";
import { formatMoney } from "../../utils/currency";
import {
  Button, StatTile, SearchInput, Select, DateInput, List, Modal, Field, Textarea,
  EmptyState, ErrorState, Skeleton, useDelayedDelete,
} from "../../ui";
import SectionHead from "./SectionHead";
import ExpenseCard from "./ExpenseCard";
import ExpenseForm, { expenseFailure } from "./ExpenseForm";
import { showErrorToast, showSuccessToast } from '../../utils/toast';   // fix(toast): the shared toast
import "./invoicing.css";
import "./expenses.css";

const COLUMNS = [
  { key: "date", label: "Date", width: "minmax(0, 0.9fr)", sortable: true },
  { key: "expense", label: "Expense", width: "minmax(0, 2fr)", role: "primary" },
  { key: "project", label: "Project", width: "minmax(0, 1fr)" },
  { key: "supplier", label: "Supplier", width: "minmax(0, 1fr)" },
  { key: "billable", label: "Billable", width: "minmax(0, 0.9fr)" },
  { key: "amount", label: "Amount", width: "minmax(0, 1fr)", align: "end" },
  { key: "status", label: "Status", width: "minmax(0, 0.9fr)", role: "status" },
  { key: "receipt", label: "Receipt", width: "minmax(0, 0.8fr)" },
  { key: "actions", label: "", width: "max-content", role: "actions" },
];

// { GBP: 120, EUR: 40 } for a list of expenses.
function sumByCurrency(list, base) {
  const out = {};
  for (const e of list) {
    const code = e.currency || base;
    out[code] = (out[code] || 0) + Number(e.amount || 0);
  }
  return out;
}

// "£120.00 plus €40.00", the base currency first; "£0.00" when there is nothing.
function moneyLine(map, base) {
  const codes = Object.keys(map).sort((a, b) => (a === base ? -1 : b === base ? 1 : a.localeCompare(b)));
  if (!codes.length) return formatMoney(0, base);
  return codes.map(c => formatMoney(map[c], c)).join(" plus ");
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export default function ExpenseList() {
  useAutoTour('expenses');
  useChatIntro('expenses');
  // loaded / loadError: read when the provider supplies them (see the report for the context patch);
  // without them the list behaves as before, as loaded.
  const { expenses, setExpenses, refresh, loaded, loadError } = useExpenses();
  const { currentUser } = useCurrentUser();
  // TeamBooks P2-3: backend-computed truth (owner or finance) — matches the
  // /api/expenses/:id/approve gate, so admins don't see buttons that 403.
  const canApprove = !!currentUser?.canApproveExpenses;
  const currency = currentUser?.baseCurrency || currentUser?.currencyDisplay || "GBP";
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  // Reject: the expense, the reason being typed, and the dialog's own busy / error state.
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [billableFilter, setBillableFilter] = useState("all");

  // Date column sort
  const [sortDir, setSortDir] = useState("desc");
  const [allProjects, setAllProjects] = useState([]);

  const { contacts: allContacts } = useContacts();
  // Build lookup maps for project/contact names
  const projectsLookup = Object.fromEntries(allProjects.map(p => [p.id, p.name]));
  const contactsLookup = Object.fromEntries(allContacts.map(c => [c.id, c.name]));

  useEffect(() => {
    let alive = true;
    getProjects()
      .then((items) => {
        if (alive) setAllProjects(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (alive) setAllProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  // Decision 7: the row goes at once, Undo for 6 seconds, and only then is the DELETE sent.
  const del = useDelayedDelete({
    send: (e, { keepalive }) => api(`/api/expenses/${e.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (e) => setExpenses(prev => prev.filter(x => x.id !== e.id)),
    describe: (e) => `Deleted ${e.title}.`,
    failMessage: 'That expense could not be deleted.',
  });

  // Put one expense back as it was before an optimistic change.
  const restore = (before) => setExpenses(prev => prev.map(e => e.id === before.id ? before : e));

  // ─── CRUD ───────────────────────────────────────────────────────────────────
  async function handleSave(data) {
    // Map snake_case form fields → camelCase API fields
    const payload = {
      title: data.title,
      amount: data.amount,
      date: data.date,
      category: data.category,
      projectId: data.project_id ?? null,
      contactId: data.contact_id ?? null,
      receiptUrl: data.receipt_url ?? null,
      notes: data.notes ?? '',
      isBillable: data.is_billable ?? false,
      isBilled: data.is_billed ?? false,
      taxRate: data.tax_rate ?? 0,
    };

    if (data.id) {
      const before = expenses.find(e => e.id === data.id);
      setExpenses(prev => prev.map(e => e.id === data.id ? { ...e, ...data } : e));
      try {
        await api(`/api/expenses/${data.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showSuccessToast("Expense updated.");
      } catch (err) {
        if (before) restore(before);
        showErrorToast(expenseFailure(err, 'update that expense'));
        return;                       // the form stays open, holding her figures
      }
    } else {
      const tempId = `temp-${Date.now()}`;
      const optimistic = {
        title: '', amount: 0, date: new Date().toISOString().slice(0, 10),
        category: '', project_id: null, contact_id: null, receipt_url: null,
        notes: '', is_billable: false, is_billed: false, tax_rate: 0,
        created_at: new Date().toISOString(),
        ...data,
        id: tempId,
      };
      setExpenses(prev => [optimistic, ...prev]);
      try {
        const res = await api('/api/expenses', { method: 'POST', body: JSON.stringify(payload) });
        const d = await res.json().catch(() => ({}));
        // A 2xx without the expense would leave a row whose id belongs to nothing.
        if (!d?.expense) throw new Error('The server did not return the saved expense. Refresh to check before adding it again.');
        setExpenses(prev => prev.map(e => e.id === tempId ? {
          ...optimistic,
          id: Number(d.expense.id),
          approval_status: d.expense.approvalStatus ?? optimistic.approval_status ?? 'approved',
        } : e));
        showSuccessToast("Expense saved.");
      } catch (err) {
        setExpenses(prev => prev.filter(e => e.id !== tempId));
        showErrorToast(expenseFailure(err, 'save that expense'));
        return;
      }
    }
    setShowForm(false);
    setEditTarget(null);
  }

  function handleEdit(expense) {
    setEditTarget(expense);
    setShowForm(true);
  }

  // ─── Approval (owner/finance only) ────────────────────────────────────────────
  async function handleApprove(expense) {
    setExpenses(prev => prev.map(e => e.id === expense.id ? { ...e, approval_status: 'approved', rejection_reason: null } : e));
    try {
      await api(`/api/expenses/${expense.id}/approve`, { method: 'PUT' });
      showSuccessToast("Expense approved.");
    } catch (err) {
      restore(expense);
      showErrorToast(expenseFailure(err, 'approve that expense'));
    }
  }

  function openReject(expense) {
    setRejectTarget(expense);
    setRejectReason("");
    setRejectError(null);
  }

  async function confirmReject() {
    const expense = rejectTarget;
    if (!expense || rejecting) return;
    const reason = rejectReason.trim();
    setRejecting(true);
    setRejectError(null);
    try {
      await api(`/api/expenses/${expense.id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) });
      setExpenses(prev => prev.map(e => e.id === expense.id ? { ...e, approval_status: 'rejected', rejection_reason: reason || null } : e));
      setRejectTarget(null);
      showSuccessToast("Expense rejected.");
    } catch (err) {
      // Said in the dialog, beside the button that failed; the dialog stays open.
      setRejectError(expenseFailure(err, 'reject that expense'));
    } finally {
      setRejecting(false);
    }
  }

  // ─── Filtered + sorted list ─────────────────────────────────────────────────
  const { isHidden } = del;
  const live = useMemo(() => expenses.filter(e => !isHidden(e.id)), [expenses, isHidden]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = live.filter(e => {
      const matchSearch = !q || e.title?.toLowerCase().includes(q);
      const matchCat = !catFilter || e.category === catFilter;
      const matchProject = !projectFilter
        ? true
        : projectFilter === "none" ? !e.project_id : String(e.project_id) === projectFilter;
      const matchDateFrom = !dateFrom || e.date >= dateFrom;
      const matchDateTo = !dateTo || e.date <= dateTo;
      const matchBillable = billableFilter === "all"
        ? true
        : billableFilter === "billable" ? e.is_billable : !e.is_billable;
      return matchSearch && matchCat && matchProject && matchDateFrom && matchDateTo && matchBillable;
    });

    list.sort((a, b) => {
      const d = String(a.date || '').localeCompare(String(b.date || ''));
      return sortDir === "asc" ? d : -d;
    });

    return list;
  }, [live, search, catFilter, projectFilter, dateFrom, dateTo, billableFilter, sortDir]);

  // ─── Summary ────────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const billableList = filtered.filter(e => e.is_billable);
    const nonBillableList = filtered.filter(e => !e.is_billable);
    return {
      total: moneyLine(sumByCurrency(filtered, currency), currency),
      billable: moneyLine(sumByCurrency(billableList, currency), currency),
      nonBillable: moneyLine(sumByCurrency(nonBillableList, currency), currency),
      billableCount: billableList.length,
      nonBillableCount: nonBillableList.length,
    };
  }, [filtered, currency]);

  const pendingCount = canApprove ? live.filter(e => e.approval_status === 'pending').length : 0;
  const hasActiveFilter = search || catFilter || projectFilter || dateFrom || dateTo || billableFilter !== "all";
  const loading = loaded === false && !loadError;
  const openNew = () => { setEditTarget(null); setShowForm(true); };

  const subline = loading || loadError
    ? null
    : [plural(live.length, "expense"), pendingCount ? `${pendingCount} awaiting approval` : null].filter(Boolean).join(" · ");

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="invc-section">
      <SectionHead
        title="Expenses"
        subline={subline}
        action={<Button variant="create" data-tour-id="expenses-new" onClick={openNew}>+ New expense</Button>}
      >
        <HelpButton tourId="expenses" />
      </SectionHead>

      {loading ? (
        <Skeleton lines={4} label="Loading expenses" />
      ) : loadError ? (
        <ErrorState title="Your expenses did not load" message={messageFromError(loadError)} onRetry={() => refresh?.()} />
      ) : (
        <>
          <section className="invc-kpis" data-tour-id="expenses-summary" aria-label="Expense totals">
            <StatTile card color="pastel-orange" label="Total expenses" value={summary.total} hint={`${filtered.length} shown`} />
            <StatTile card color="pastel-yellow" label="Billable" value={summary.billable} hint={plural(summary.billableCount, "expense")} />
            <StatTile card color="pastel-neutral" label="Non-billable" value={summary.nonBillable} hint={plural(summary.nonBillableCount, "expense")} />
          </section>

          <div className="invc-bar" data-tour-id="expenses-filter">
            <SearchInput label="Search expenses" placeholder="Search by title…" value={search} onChange={e => setSearch(e.target.value)} />
            <div className="invc-bar__filters exp-filters">
              <Select aria-label="Category" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
                <option value="">All categories</option>
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
              {allProjects.length > 0 && (
                <Select aria-label="Project" value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
                  <option value="">All projects</option>
                  <option value="none">Unattached</option>
                  {allProjects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </Select>
              )}
              <span className="exp-dates">
                <DateInput aria-label="From date" title="From date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <span className="exp-dates__sep" aria-hidden="true">to</span>
                <DateInput aria-label="To date" title="To date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </span>
              <Select aria-label="Billable" value={billableFilter} onChange={e => setBillableFilter(e.target.value)}>
                <option value="all">All expenses</option>
                <option value="billable">Billable only</option>
                <option value="non-billable">Non-billable only</option>
              </Select>
              {hasActiveFilter && (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => {
                    setSearch(""); setCatFilter(""); setProjectFilter("");
                    setDateFrom(""); setDateTo(""); setBillableFilter("all");
                  }}
                >
                  Clear all
                </Button>
              )}
            </div>
          </div>

          <div data-tour-id="expenses-list">
            {live.length === 0 ? (
              <EmptyState icon="🧾" title="No expenses yet"
                action={<Button variant="create" onClick={openNew}>+ New expense</Button>}>
                Add what the business spends, with the receipt, and mark what you will bill on.
              </EmptyState>
            ) : filtered.length === 0 ? (
              <p className="invc-muted">No expenses match your filters.</p>
            ) : (
              <List label="Expenses" columns={COLUMNS} sort={{ key: "date", dir: sortDir }} onSortChange={s => setSortDir(s.dir)}>
                {filtered.map(expense => (
                  <ExpenseCard
                    key={expense.id}
                    expense={expense}
                    currency={currency}
                    onEdit={handleEdit}
                    onDelete={del.remove}
                    onApprove={handleApprove}
                    onReject={openReject}
                    canApprove={canApprove}
                    projectsLookup={projectsLookup}
                    contactsLookup={contactsLookup}
                  />
                ))}
              </List>
            )}
          </div>
        </>
      )}

      {/* Form modal */}
      {showForm && (
        <ExpenseForm
          expense={editTarget}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}

      <Modal
        open={!!rejectTarget}
        onClose={rejecting ? undefined : () => setRejectTarget(null)}
        title="Reject this expense?"
        color="pastel-coral"
        width={480}
        footer={<>
          <Button variant="tertiary" onClick={() => setRejectTarget(null)} disabled={rejecting}>Cancel</Button>
          <Button variant="confirm" onClick={confirmReject} disabled={rejecting} aria-busy={rejecting || undefined}>
            {rejecting ? "Rejecting…" : "Reject expense"}
          </Button>
        </>}
      >
        <div className="exp-form">
          <p className="invc-muted">
            {rejectTarget ? <><strong>{rejectTarget.title}</strong>, {formatMoney(rejectTarget.amount, rejectTarget.currency || currency)}. </> : null}
            The person who added it will see that it was rejected, with your reason.
          </p>
          <Field label="Reason (optional)">
            <Textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-autofocus="" />
          </Field>
          {rejectError && <p className="invc-error" role="alert">{rejectError}</p>}
        </div>
      </Modal>
    </div>
  );
}
